import { get } from "lodash"
import { SelectedEtf } from "helpers/types/instruments/selected"
import { IsoDate } from "helpers/types/dates"
import { OkReduxDataResult } from "model/state"
import { CompositionByNode, getCompositionType } from "./composition"
import { PositionSetMetadata } from "./types"

interface SecurityData {
  model: {
    security: {
      availability: {
        to: IsoDate
        from: IsoDate
      }
      descriptors: {
        name: string
        assetSubclass: "etf" | null
        hasComposition: boolean
        internalId: string
        ticker: string
        mic: string
      }
    }
    simulation: {
      composition: [{
        compositionBy: CompositionByNode
      }]
    }
  }
}

export interface EtfMetadata extends PositionSetMetadata {
  type: "ETF" | "SECURITY"
  hasComposition: boolean
  internalId: string // internal ID
  ticker: string // Ticker
  mic: string // MIC
}

const SECURITY_QUERY = `query ($modelId: String!, $id: String!, $currentDate: Date!, $segment: Segment) {
  model (id: $modelId) {
    security (internalId: $id) {
      availability {
        to
        from
      }
      descriptors (on: $currentDate) {
        name
        assetSubclass
        hasComposition
        internalId
        ticker
        mic
      }
    }
    simulation (positionSet: {
      dates: { date: $currentDate, equities: { id: { internalId: $id }, economicExposure: 1 } },
      expand: true,
      segment: $segment
    }, from: $currentDate, to: $currentDate) {
      composition {
        compositionBy (groupBy: LONG_SHORT) {
          id
        }
      }
    }
  }
}`

function etfSelect(data: OkReduxDataResult<SecurityData>): EtfMetadata {
  const assetSubclass = get(data, "data.model.security.descriptors.assetSubclass")
  const type = assetSubclass === "etf" ? "ETF" : "SECURITY"

  return {
    name: get(data, "data.model.security.descriptors.name"),
    earliestDate: get(data, "data.model.security.availability.from"),
    latestDate: get(data, "data.model.security.availability.to"),
    // [TODO:TS]
    unavailable: get(data, "data.model.security.availability") === null,
    hasComposition: get(data, "data.model.security.descriptors.hasComposition"),
    internalId: get(data, "data.model.security.descriptors.internalId"),
    ticker: get(data, "data.model.security.descriptors.ticker"),
    mic: get(data, "data.model.security.descriptors.mic"),
    compositionType: getCompositionType(data),
    type,
  }
}

export function getSecurityQueryDefinition(positionSet: SelectedEtf, modelId: string, currentDate: IsoDate) {
  const variables = { modelId, currentDate, id: positionSet.id, segment: positionSet.segment }

  return {
    query: SECURITY_QUERY,
    variables,
    queryKey: ["position-set", "metadata", "security", variables],
    select: etfSelect,
  }
}
